import find from "lodash/find";
import some from "lodash/some";
import { useKPIResults } from "PFCore/hooks/queries/kpi";
import { KPIResult, KPIStatus } from "PFCore/types/kpi";
import { IconName } from "PFTheme/graphics/icons";
import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";
import { createStore } from "zustand";

import { useActivityPermissions, useActivityTemplateType } from "../../../hooks";
import { ActivityTab } from "../../../types/activity_tab";
import { useCurrentActivity } from "../../hooks/use_current_activity";
import { ActivityPageTab, ActivityTabsStoreType } from "./activity_tabs_store.types";
import { ActivityTabsStoreContext } from "./activity_tabs_store_context";

const ACTIVITIES_PATH = "activities";

export const ActivityTabsStoreProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation("activities", { keyPrefix: "show.tabs" });
  const navigate = useNavigate();
  const location = useLocation();
  const { section } = useParams<{ section: ActivityTab }>();
  const activity = useCurrentActivity();

  const tabId = section || ActivityTab.Overview;

  const {
    canSeeMatches,
    canSeeShortlist,
    canSeeVacancies,
    canSeeFeedback,
    canSeeInterested,
    canSeeRoles,
    canSeeEconomics,
    canSeeKPIs,
    canSeeHistory,
    permissionsReady
  } = useActivityPermissions(activity);
  const { isAudit } = useActivityTemplateType(activity);

  const { data: kpiTabIcon } = useKPIResults<IconName | undefined>(
    { engagementId: activity.id },
    {
      enabled: canSeeKPIs,
      select: (data) => {
        const hasStatus = (statuses: KPIStatus[]) =>
          some(data, ({ status }: KPIResult) => statuses.includes(status));

        if (hasStatus([KPIStatus.ApprovalRequired, KPIStatus.ApprovalRejected])) {
          return "error";
        }

        if (hasStatus([KPIStatus.ApprovalRequested])) {
          return "warning";
        }
      }
    }
  );

  const tabValues: Record<string, ActivityPageTab> = useMemo(
    () => ({
      matches: {
        id: ActivityTab.Matches,
        title: t("matches")
      },
      shortlist: {
        id: ActivityTab.Shortlist,
        title: t("shortlist")
      },
      roles: {
        id: ActivityTab.Roles,
        title: t("roles")
      },
      overview: {
        id: ActivityTab.Overview,
        title: t("overview")
      },
      interested: {
        id: ActivityTab.Interested,
        title: t("interested")
      },
      vacancies: {
        id: ActivityTab.Vacancies,
        title: t("vacancies")
      },
      feedback: {
        id: ActivityTab.Feedback,
        title: t("feedback")
      },
      economics: {
        id: ActivityTab.Economics,
        title: t("economics")
      },
      kpi: {
        id: ActivityTab.KPI,
        title: t("kpi"),
        icon: kpiTabIcon,
        iconColor: kpiTabIcon === "error" ? "paletteRed1" : "paletteOrange1"
      },
      history: {
        id: ActivityTab.History,
        title: t("history")
      }
    }),
    [t, kpiTabIcon]
  );

  const tabs = useMemo(() => {
    const result: ActivityPageTab[] = [tabValues.overview];

    if (canSeeInterested && !isAudit) {
      result.push(tabValues.interested);
    }

    if (canSeeMatches) {
      result.push(tabValues.matches);
    }

    if (canSeeShortlist) {
      result.push(tabValues.shortlist);
    }

    if (canSeeRoles) {
      result.push(tabValues.roles);
    }

    if (canSeeVacancies && !isAudit) {
      result.push(tabValues.vacancies);
    }

    if (canSeeFeedback && !isAudit) {
      result.push(tabValues.feedback);
    }

    if (canSeeEconomics) {
      result.push(tabValues.economics);
    }

    if (canSeeKPIs) {
      result.push(tabValues.kpi);
    }

    if (canSeeHistory) {
      result.push(tabValues.history);
    }

    return result;
  }, [
    isAudit,
    canSeeMatches,
    canSeeShortlist,
    canSeeRoles,
    canSeeVacancies,
    canSeeFeedback,
    canSeeEconomics,
    canSeeKPIs,
    canSeeHistory,
    canSeeInterested,
    tabValues
  ]);

  const [store] = useState(
    createStore<ActivityTabsStoreType>()((set) => ({
      tabs,
      tabActiveId: tabId,
      actions: {
        changeTab: (tabId: ActivityPageTab["id"]) => {
          const utmSource = location?.state?.utmSource;

          if (tabId === ActivityTab.Overview) {
            navigate(
              {
                pathname: `/${ACTIVITIES_PATH}/${activity.id}`,
                search: location.search
              },
              {
                state: { utmSource }
              }
            );
          } else {
            navigate(
              {
                pathname: `/${ACTIVITIES_PATH}/${activity.id}/${tabId}`,
                search: location.search
              },
              {
                state: { utmSource }
              }
            );
          }

          set({ tabActiveId: tabId as ActivityTab });
        }
      }
    }))
  );

  useEffect(() => {
    store.setState({ tabs });
  }, [tabs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (permissionsReady && !find(tabs, { id: tabId })) {
      store.getState().actions.changeTab(ActivityTab.Overview);
    }
  }, [tabs, permissionsReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (store.getState().tabActiveId !== tabId) {
      store.setState({ tabActiveId: tabId as ActivityTab });
    }
  }, [tabId]); // eslint-disable-line react-hooks/exhaustive-deps

  return <ActivityTabsStoreContext.Provider value={store}>{children}</ActivityTabsStoreContext.Provider>;
};
